import React, { useMemo } from "react";
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell } from "recharts";
import { fmtEUR } from "../../lib/api";

const MESI = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const PIE = ["#3B82F6", "#22C55E", "#E9C46A", "#F97316", "#94A3B8", "#14B8A6", "#F43F5E", "#0EA5E9"];
export const card = "rounded-2xl border border-[#D4A72C]/20 bg-gradient-to-b from-[#0E1320] to-[#07090F] p-4";

export function YearCalendar({ year, services, selected, onSelect }) {
  const byDay = useMemo(() => {
    const m = {};
    services.forEach(s => { const k = s.date; const t = s.status === "Cancellata" ? "c" : s.dispo ? "d" : "s"; m[k] = m[k] || new Set(); m[k].add(t); });
    return m;
  }, [services]);
  const dot = (k) => { const v = byDay[k]; if (!v) return "bg-white/[0.07]"; if (v.has("s")) return "bg-[#3B82F6]"; if (v.has("d")) return "bg-[#A855F7]"; return "bg-rose-500"; };
  return (
    <section className={card} data-testid="sa-calendar">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h2 className="font-display text-base text-white">Calendario servizi {year}</h2>
        <div className="flex flex-wrap gap-3 text-[10.5px] text-slate-400">
          {[["bg-[#3B82F6]", "Servizio"], ["bg-[#A855F7]", "DISPO"], ["bg-rose-500", "Annullato"], ["bg-white/20", "Nessun servizio"]].map(([c, l]) => <span key={l} className="inline-flex items-center gap-1"><i className={`w-2 h-2 rounded-full ${c}`} />{l}</span>)}
        </div>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-6 md:grid-cols-12 gap-2">
        {MESI.map((m, mi) => {
          const days = new Date(Date.UTC(year, mi + 1, 0)).getUTCDate();
          return (
            <div key={m}>
              <div className="text-[10px] text-slate-400 text-center mb-1">{m}</div>
              <div className="grid grid-cols-5 gap-[3px] justify-items-center">
                {Array.from({ length: days }, (_, i) => {
                  const k = `${year}-${String(mi + 1).padStart(2, "0")}-${String(i + 1).padStart(2, "0")}`;
                  return <button key={k} type="button" title={k.split("-").reverse().join("/")} onClick={() => onSelect(selected === k ? null : k)} data-testid={`sa-day-${k}`}
                    className="w-4 h-4 md:w-2.5 md:h-2.5 flex items-center justify-center"><i className={`block w-2.5 h-2.5 md:w-2 md:h-2 rounded-full ${dot(k)} ${selected === k ? "ring-2 ring-[#E9C46A]" : ""}`} /></button>;
                })}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export function MonthlyChart({ months, hoursByMonth }) {
  const data = months.map((m, i) => ({ name: MESI[i], servizi: m.services_count, compensi: Math.round((m.driver_share + m.uber_driver_share + m.uber_cash_driver_share) * 100) / 100, ore: hoursByMonth[i] }));
  return (
    <section className={card} data-testid="sa-monthly">
      <div className="flex items-center justify-between mb-2"><h2 className="font-display text-base text-white">Servizi per mese</h2>
        <div className="flex gap-3 text-[10.5px] text-slate-400"><span><i className="inline-block w-2 h-2 rounded-full bg-[#3B82F6] mr-1" />Servizi</span><span><i className="inline-block w-2 h-2 rounded-full bg-[#A855F7] mr-1" />Ore DISPO</span><span><i className="inline-block w-2 h-2 rounded-full bg-[#E9C46A] mr-1" />Compensi €</span></div></div>
      <div className="h-[200px]"><ResponsiveContainer><ComposedChart data={data} margin={{ top: 6, right: 0, left: -18, bottom: 0 }}>
        <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
        <XAxis dataKey="name" tick={{ fill: "#94A3B8", fontSize: 10 }} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={4} />
        <YAxis yAxisId="l" tick={{ fill: "#94A3B8", fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
        <YAxis yAxisId="r" orientation="right" hide />
        <Tooltip contentStyle={{ background: "#0B0A08", border: "1px solid rgba(212,167,44,0.3)", borderRadius: 8, fontSize: 12 }} formatter={(v, k) => k === "compensi" ? fmtEUR(v) : v} />
        <Bar yAxisId="l" dataKey="servizi" fill="#3B82F6" radius={[3, 3, 0, 0]} />
        <Bar yAxisId="l" dataKey="ore" fill="#A855F7" radius={[3, 3, 0, 0]} />
        <Line yAxisId="r" dataKey="compensi" stroke="#E9C46A" strokeWidth={2} dot={{ r: 2.5, fill: "#E9C46A" }} />
      </ComposedChart></ResponsiveContainer></div>
    </section>
  );
}

export function TypePie({ services }) {
  const rows = useMemo(() => {
    const m = {};
    services.filter(s => s.status !== "Cancellata").forEach(s => { const k = s.service_type || "Non indicato"; m[k] = (m[k] || 0) + 1; });
    return Object.entries(m).sort((a, b) => b[1] - a[1]).map(([name, v], i) => ({ name, v, c: PIE[i % PIE.length] }));
  }, [services]);
  const tot = rows.reduce((s, r) => s + r.v, 0);
  return (
    <section className={card} data-testid="sa-types">
      <h2 className="font-display text-base text-white mb-2">Tipologia servizi</h2>
      <div className="grid grid-cols-[130px_1fr] gap-3 items-center">
        <div className="relative h-[130px]"><ResponsiveContainer><PieChart><Pie data={rows.length ? rows : [{ v: 1, c: "#1E293B" }]} dataKey="v" innerRadius="68%" outerRadius="95%" stroke="none">
          {(rows.length ? rows : [{ c: "#1E293B" }]).map((r, i) => <Cell key={i} fill={r.c} />)}</Pie></PieChart></ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-center"><span className="text-white text-xl font-mono-num">{tot}</span><span className="text-[10px] text-slate-400">Servizi</span></div></div>
        <div className="space-y-1">{rows.map(r => <div key={r.name} className="flex items-center gap-2 text-[12px]"><i className="w-2 h-2 rounded-full shrink-0" style={{ background: r.c }} /><span className="flex-1 text-slate-200 truncate">{r.name}</span><span className="text-slate-400 font-mono-num">{Math.round((r.v / tot) * 100)}%</span></div>)}
          {!rows.length && <div className="text-xs text-slate-500">Nessun servizio</div>}</div>
      </div>
    </section>
  );
}
